import { useMemo, useState } from "react";
import { useResource } from "../lib/hooks";
import { InitialsAvatar } from "../components/InitialsAvatar";
import { OnboardingDetail } from "../components/OnboardingDetail";

interface Count { done: number; total: number }
interface Person {
    employeeId: string;
    name: string;
    hasLoggedIn: boolean;
    adminPct: number;
    userPct: number;
    state: "in_progress" | "ready" | "approved";
    approvedAt: string | null;
    sections: { employeeInfo: Count; profile: Count; documents: Count; policies: Count };
}

const bar = (label: string, pctVal: number) => (
    <div className="ob-row-bar" title={`${label} ${pctVal}%`}>
        <span className="ob-row-bar-label">{label}</span>
        <span className="onboard-meter-bar"><i style={{ width: `${pctVal}%` }} /></span>
        <span className="ob-row-bar-pct">{pctVal}%</span>
    </div>
);

const frac = (c: Count) => `${c.done}/${c.total}`;

export function StaffOnboarding() {
    const people = useResource<Person[]>("/onboarding/people");
    const [tab, setTab] = useState<"active" | "approved">("active");
    const [layout, setLayout] = useState<"list" | "table">("list");
    const [selected, setSelected] = useState<string | null>(null);
    const [sortKey, setSortKey] = useState<string>("name");
    const [sortDir, setSortDir] = useState<1 | -1>(1);

    const all = people.data ?? [];
    const inTab = all.filter((p) => (tab === "approved" ? p.state === "approved" : p.state !== "approved"));

    const sortVal = (p: Person, key: string): string | number => {
        switch (key) {
            case "name": return p.name.toLowerCase();
            case "admin": return p.adminPct;
            case "user": return p.userPct;
            case "employeeInfo": return p.sections.employeeInfo.done / Math.max(1, p.sections.employeeInfo.total);
            case "profile": return p.sections.profile.done / Math.max(1, p.sections.profile.total);
            case "documents": return p.sections.documents.done / Math.max(1, p.sections.documents.total);
            case "policies": return p.sections.policies.done / Math.max(1, p.sections.policies.total);
            case "state": return p.state;
            default: return p.name;
        }
    };
    const sorted = useMemo(() => [...inTab].sort((a, b) => {
        const av = sortVal(a, sortKey), bv = sortVal(b, sortKey);
        return (av < bv ? -1 : av > bv ? 1 : 0) * sortDir;
    }), [inTab, sortKey, sortDir]);

    const toggleSort = (key: string) => {
        if (sortKey === key) setSortDir((d) => (d === 1 ? -1 : 1));
        else { setSortKey(key); setSortDir(1); }
    };

    const inProgress = sorted.filter((p) => p.state === "in_progress");
    const ready = sorted.filter((p) => p.state === "ready");

    const row = (p: Person) => (
        <button key={p.employeeId} className={`ob-person-row ${selected === p.employeeId ? "selected" : ""}`}
            onClick={() => setSelected(p.employeeId)}>
            <InitialsAvatar name={p.name} size={30} />
            <span className="ob-person-name">{p.name}</span>
            <span className="ob-person-bars">{bar("Admin", p.adminPct)}{bar("User", p.userPct)}</span>
        </button>
    );

    const header = (label: string, key: string) => (
        <th onClick={() => toggleSort(key)} style={{ cursor: "pointer", whiteSpace: "nowrap" }}>
            {label}{sortKey === key ? (sortDir === 1 ? " ▲" : " ▼") : ""}
        </th>
    );

    return (
        <div>
            <div className="toolbar">
                <div className="pill-tabs">
                    <button className={`tab ${tab === "active" ? "active" : ""}`} onClick={() => setTab("active")}>Active</button>
                    <button className={`tab ${tab === "approved" ? "active" : ""}`} onClick={() => setTab("approved")}>Approved</button>
                </div>
                <div className="spacer" />
                <button className="btn ghost small" onClick={() => setLayout((l) => (l === "list" ? "table" : "list"))}>
                    {layout === "list" ? "Table view" : "List view"}
                </button>
            </div>

            {people.loading ? <div className="card"><div className="card-body muted">Loading…</div></div>
            : inTab.length === 0 ? <div className="card"><div className="card-body empty">Nobody here.</div></div>
            : layout === "table" ? (
                <div className="card"><div className="card-body flush" style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%" }}>
                        <thead><tr>
                            {header("Name", "name")}
                            {header("Employee info", "employeeInfo")}
                            {header("Profile", "profile")}
                            {header("Documents", "documents")}
                            {header("Policies", "policies")}
                            {header("Admin", "admin")}
                            {header("User", "user")}
                            {header("State", "state")}
                        </tr></thead>
                        <tbody>
                            {sorted.map((p) => (
                                <tr key={p.employeeId} style={{ cursor: "pointer" }} onClick={() => setSelected(p.employeeId)}>
                                    <td>{p.name}</td>
                                    <td style={{ textAlign: "center" }}>{frac(p.sections.employeeInfo)}</td>
                                    <td style={{ textAlign: "center" }}>{frac(p.sections.profile)}</td>
                                    <td style={{ textAlign: "center" }}>{frac(p.sections.documents)}</td>
                                    <td style={{ textAlign: "center" }}>{frac(p.sections.policies)}</td>
                                    <td style={{ textAlign: "center" }}>{p.adminPct}%</td>
                                    <td style={{ textAlign: "center" }}>{p.userPct}%</td>
                                    <td style={{ textAlign: "center" }}>
                                        <span className={`chip ${p.state === "approved" ? "pass" : p.state === "ready" ? "info" : "warn"}`}>
                                            {p.state === "in_progress" ? "In progress" : p.state === "ready" ? "Ready" : "Approved"}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div></div>
            ) : (
                <div className="grid cols-2" style={{ alignItems: "start" }}>
                    <div className="card"><div className="card-body">
                        {tab === "active" && (
                            <>
                                <h3 className="ob-section-head">Ready for review &amp; approval <span className="chip info">{ready.length}</span></h3>
                                {ready.length === 0 ? <p className="muted">None ready.</p> : ready.map(row)}
                                <h3 className="ob-section-head" style={{ marginTop: "1rem" }}>In progress <span className="chip warn">{inProgress.length}</span></h3>
                                {inProgress.length === 0 ? <p className="muted">None in progress.</p> : inProgress.map(row)}
                            </>
                        )}
                        {tab === "approved" && sorted.map(row)}
                    </div></div>
                    <div>
                        {selected
                            ? <OnboardingDetail employeeId={selected} onChanged={() => people.reload()} />
                            : <div className="card"><div className="card-body muted">Select a person to see their onboarding detail.</div></div>}
                    </div>
                </div>
            )}
        </div>
    );
}
